import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Row, Col } from 'react-bootstrap';
import { FaPlay, FaArrowLeft, FaClock, FaTrophy } from 'react-icons/fa';
import questionsData from '../data/json/combined_fin_vocab.json';

const QuizList = () => {
    const { category } = useParams();
    const { partOfSpeech, difficulty } = useParams();
    
    let filteredQuestions = [];

    if (category) {
        filteredQuestions = questionsData.filter(item => item.category === category);
    } else if (partOfSpeech && difficulty) {
        filteredQuestions = questionsData.filter(item => item.part_of_speech === partOfSpeech && item.difficulty === difficulty);
    }

    const totalRanges = Math.ceil(filteredQuestions.length / 10);
    const title = category ? category : `${partOfSpeech} (${difficulty})`;
    
    // 難易度に応じた色を決定
    const getDifficultyColor = () => {
        if (category) return 'primary';
        if (difficulty === '初級') return 'success';
        if (difficulty === '中級') return 'warning';
        return 'danger';
    };

    const difficultyColor = getDifficultyColor();

    return (
        <div className="modern-container">
            <div className="modern-section">
                {/* Header */}
                <div className="fade-in-up" style={{ marginBottom: 'var(--spacing-2xl)' }}>
                    <div style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--spacing-md)',
                        marginBottom: 'var(--spacing-lg)'
                    }}>
                        <Link 
                            to="/" 
                            className="modern-btn modern-btn-secondary"
                            style={{ 
                                display: 'flex',
                                alignItems: 'center',
                                gap: 'var(--spacing-xs)',
                                padding: 'var(--spacing-sm) var(--spacing-md)'
                            }}
                        >
                            <FaArrowLeft />
                            戻る
                        </Link>
                        <span className={`modern-badge modern-badge-${difficultyColor}`}>
                            {category ? 'カテゴリー' : difficulty}
                        </span>
                    </div>
                    
                    <h2 style={{ 
                        background: `var(--${difficultyColor}-gradient)`,
                        WebkitBackgroundClip: 'text',
                        WebkitTextFillColor: 'transparent',
                        backgroundClip: 'text',
                        marginBottom: 'var(--spacing-md)',
                        textAlign: 'center'
                    }}>
                        {title} クイズ一覧
                    </h2>
                    
                    <div className="text-center">
                        <p style={{ 
                            color: 'var(--text-secondary)',
                            fontSize: '1.1rem'
                        }}>
                            全 {filteredQuestions.length} 単語・{totalRanges} セット
                        </p>
                    </div>
                </div>

                {/* Quiz Sets Grid */}
                <Row>
                    {[...Array(totalRanges)].map((_, rangeIndex) => {
                        const startWord = (rangeIndex) * 10 + 1;
                        const endWord = Math.min((rangeIndex + 1) * 10, filteredQuestions.length);
                        const questionsInSet = endWord - startWord + 1;
                        
                        // セットの説明的な名前を生成
                        const getSetTitle = () => {
                            if (category) {
                                // カテゴリの場合
                                const setNames = [
                                    "基礎編", "実践編", "応用編", "上級編", "マスター編",
                                    "エキスパート編", "プロフェッショナル編", "スペシャリスト編"
                                ];
                                return setNames[rangeIndex] || `レベル ${rangeIndex + 1}`;
                            } else {
                                // 品詞・難易度の場合
                                if (difficulty === '初級') {
                                    const beginnerSets = [
                                        "はじめの一歩", "基礎固め", "日常会話", "基本表現"
                                    ];
                                    return beginnerSets[rangeIndex] || `基礎 ${rangeIndex + 1}`;
                                } else if (difficulty === '中級') {
                                    const intermediateSets = [
                                        "実践力アップ", "表現力強化", "語彙拡張", "応用編"
                                    ];
                                    return intermediateSets[rangeIndex] || `中級 ${rangeIndex + 1}`;
                                } else {
                                    const advancedSets = [
                                        "上級への道", "マスターレベル", "エキスパート", "完全習得"
                                    ];
                                    return advancedSets[rangeIndex] || `上級 ${rangeIndex + 1}`;
                                }
                            }
                        };

                        // セットの説明を生成
                        const getSetDescription = () => {
                            const currentSetWords = filteredQuestions.slice(startWord - 1, endWord);
                            
                            if (currentSetWords.length > 0) {
                                const sampleWords = currentSetWords.slice(0, 3).map(w => w.word).join(', ');
                                const hasMore = currentSetWords.length > 3;
                                return `${sampleWords}${hasMore ? ' など' : ''}`;
                            }
                            return `${questionsInSet}個の単語`;
                        };

                        // 進捗度アイコンを取得
                        const getProgressIcon = () => {
                            const icons = ['🌱', '🌿', '🌳', '🏆', '👑', '💎', '⭐', '🎯'];
                            return icons[rangeIndex] || '📚';
                        };
                        
                        return (
                            <Col key={rangeIndex} xs={12} sm={6} md={4} lg={3} className="mb-4">
                                <div className="modern-card fade-in-up" style={{ 
                                    animationDelay: `${rangeIndex * 0.1}s`,
                                    height: '100%',
                                    position: 'relative',
                                    overflow: 'hidden'
                                }}>
                                    {/* Background decoration */}
                                    <div style={{
                                        position: 'absolute',
                                        top: '-50%',
                                        right: '-50%',
                                        width: '100px',
                                        height: '100px',
                                        background: `var(--${difficultyColor}-gradient)`,
                                        borderRadius: '50%',
                                        opacity: 0.1,
                                        zIndex: 0
                                    }} />
                                    
                                    <div className="modern-card-body" style={{ 
                                        display: 'flex', 
                                        flexDirection: 'column',
                                        height: '100%',
                                        position: 'relative',
                                        zIndex: 1
                                    }}>
                                        <div style={{ 
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            alignItems: 'center',
                                            marginBottom: 'var(--spacing-md)'
                                        }}>
                                            <h4 style={{ 
                                                color: 'var(--text-primary)',
                                                margin: 0,
                                                fontSize: '1.25rem'
                                            }}>
                                                {getSetTitle()}
                                            </h4>
                                            <div style={{
                                                fontSize: '2rem'
                                            }}>
                                                {getProgressIcon()}
                                            </div>
                                        </div>
                                        
                                        <div style={{ 
                                            flex: 1,
                                            marginBottom: 'var(--spacing-lg)'
                                        }}>
                                            <div style={{ 
                                                marginBottom: 'var(--spacing-sm)',
                                                color: 'var(--text-secondary)',
                                                fontSize: '0.9rem',
                                                lineHeight: 1.4
                                            }}>
                                                {getSetDescription()}
                                            </div>
                                            
                                            <div style={{ 
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 'var(--spacing-xs)',
                                                marginBottom: 'var(--spacing-sm)',
                                                color: 'var(--text-secondary)'
                                            }}>
                                                <FaTrophy size={14} />
                                                <span>単語 {startWord}-{endWord}番</span>
                                            </div>
                                            <div style={{ 
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 'var(--spacing-xs)',
                                                color: 'var(--text-muted)',
                                                fontSize: '0.9rem'
                                            }}>
                                                <FaClock size={12} />
                                                <span>{questionsInSet}問・約{Math.ceil(questionsInSet * 0.5)}分</span>
                                            </div>
                                        </div>
                                        
                                        <Link 
                                            to={`/quiz/${category ? category : `${partOfSpeech}/${difficulty}`}/${rangeIndex + 1}`} 
                                            className={`modern-btn modern-btn-${difficultyColor}`}
                                            style={{ 
                                                width: '100%',
                                                justifyContent: 'center',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 'var(--spacing-sm)'
                                            }}
                                        >
                                            <FaPlay size={14} />
                                            {getSetTitle()}を開始
                                        </Link>
                                    </div>
                                </div>
                            </Col>
                        );
                    })}
                </Row>

                {/* No quizzes available */}
                {totalRanges === 0 && (
                    <div className="modern-alert modern-alert-info fade-in-up">
                        <div style={{ textAlign: 'center' }}>
                            <div style={{ fontSize: '3rem', marginBottom: 'var(--spacing-md)' }}>
                                📚
                            </div>
                            <h4>クイズがありません</h4>
                            <p>このカテゴリーにはまだクイズが登録されていません。</p>
                            <Link to="/" className="modern-btn modern-btn-primary">
                                ホームに戻る
                            </Link>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default QuizList;
